import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { Bold, Italic, List, ListOrdered, Strikethrough, Underline } from 'lucide-react';
import { cn } from '@/shared/helpers/cn';
import { sanitizeClinicalHtml } from '@/shared/helpers/clinical-html';
import { Icon } from '../Icon';
import styles from './RichText.module.scss';

export interface RichTextProps {
  /** Conteúdo em HTML. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
  'aria-label'?: string;
  /** `id` do rótulo visível — é o que dá nome ao campo em `contenteditable`. */
  'aria-labelledby'?: string;
  className?: string;
  /** Conteúdo extra na barra — é onde o botão de ditado por IA entra. */
  toolbarExtra?: React.ReactNode;
}

/**
 * Campo de texto com formatação.
 *
 * Existe porque **as fichas do prontuário já são HTML no banco**: o sistema
 * atual usa um CKEditor em cada campo narrativo, e trocar por texto puro
 * mostraria as tags de milhares de fichas já gravadas na tela.
 *
 * O que ele não é: um editor de propósito geral. A barra tem as seis marcações
 * que um registro clínico usa, e tudo o que entra — digitado ou colado — passa
 * pela mesma allowlist do servidor. Não há cor, fonte, tamanho, tabela nem
 * imagem: são as opções do editor atual que produziram o HTML que a POC precisa
 * higienizar hoje.
 */
export const RichText = forwardRef<HTMLDivElement, RichTextProps>(function RichText(
  {
    value,
    onChange,
    disabled = false,
    invalid = false,
    placeholder,
    className,
    toolbarExtra,
    ...aria
  },
  ref,
) {
  const areaRef = useRef<HTMLDivElement | null>(null);

  /**
   * O último valor que este campo emitiu.
   *
   * É o que distingue "o valor mudou porque a pessoa está digitando" de "o valor
   * mudou por fora" — uma ficha que acabou de carregar, ou o texto que voltou do
   * ditado. Comparar com `innerHTML` não serve: a higienização **reescreve** a
   * marcação (um `<div>` que o navegador inseriu ao teclar Enter some, um `<br>`
   * vira `<br />`), então o valor emitido quase nunca é idêntico ao que está no
   * campo — e reescrever o conteúdo a cada tecla joga o cursor para o começo.
   */
  const ultimoEmitido = useRef<string | null>(null);

  /**
   * Faz o navegador separar parágrafos com `<p>`, e não com `<div>`.
   *
   * É o padrão do Chrome e do Safari envolver cada linha nova num `<div>`, que
   * não é tag de prontuário. A higienização traduz `<div>` em `<p>` de qualquer
   * forma — isto só evita a tradução no caminho mais comum.
   */
  useEffect(() => {
    executar('defaultParagraphSeparator', 'p');
  }, []);

  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    if (value === ultimoEmitido.current) return;

    area.innerHTML = value;
    ultimoEmitido.current = value;
  }, [value]);

  const emitir = useCallback(() => {
    const area = areaRef.current;
    if (!area) return;

    const limpo = sanitizeClinicalHtml(area.innerHTML);
    ultimoEmitido.current = limpo;
    onChange(limpo);
  }, [onChange]);

  /**
   * Aplica uma marcação à seleção.
   *
   * A saída passa pelo higienizador antes de virar valor, então o que o comando
   * produza de extra — um `<span style>`, um `<font>` — não sobrevive.
   */
  const aplicar = useCallback(
    (comando: string) => {
      if (disabled) return;
      areaRef.current?.focus();
      executar(comando);
      emitir();
    },
    [disabled, emitir],
  );

  /** Colar entra como texto simples: é a origem mais comum de marcação hostil. */
  const aoColar = useCallback(
    (evento: ClipboardEvent<HTMLDivElement>) => {
      evento.preventDefault();
      const texto = evento.clipboardData.getData('text/plain');
      executar('insertText', texto);
      emitir();
    },
    [emitir],
  );

  const atalhos = useCallback(
    (evento: KeyboardEvent<HTMLDivElement>) => {
      if (!(evento.metaKey || evento.ctrlKey)) return;

      const comando = ATALHOS[evento.key.toLowerCase()];
      if (!comando) return;

      evento.preventDefault();
      aplicar(comando);
    },
    [aplicar],
  );

  return (
    <div className={cn(styles.root, invalid && styles.invalid, disabled && styles.disabled, className)}>
      <div className={styles.barra} role="toolbar" aria-label="Formatação do texto">
        {BOTOES.map((botao) => (
          <button
            key={botao.comando}
            type="button"
            className={styles.botao}
            // O nome acessível é o rótulo, não o ícone: o leitor de tela precisa
            // ouvir "negrito", e a cor do ícone nunca é o único sinal.
            title={botao.rotulo}
            aria-label={botao.rotulo}
            disabled={disabled}
            // `onMouseDown` com `preventDefault` mantém a seleção: um `onClick`
            // tira o foco do campo antes de o comando rodar, e a marcação
            // recairia sobre nada.
            onMouseDown={(evento) => {
              evento.preventDefault();
            }}
            onClick={() => {
              aplicar(botao.comando);
            }}
          >
            <Icon as={botao.icone} size={16} />
          </button>
        ))}
        {toolbarExtra ? <div className={styles.extra}>{toolbarExtra}</div> : null}
      </div>

      <div
        ref={(elemento) => {
          areaRef.current = elemento;
          if (typeof ref === 'function') ref(elemento);
          else if (ref) ref.current = elemento;
        }}
        className={styles.area}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-disabled={disabled || undefined}
        data-placeholder={placeholder}
        onInput={emitir}
        onBlur={emitir}
        onPaste={aoColar}
        onKeyDown={atalhos}
        {...aria}
      />
    </div>
  );
});

/**
 * Chama `execCommand` quando o ambiente o oferece.
 *
 * `execCommand` está formalmente obsoleto e sem substituto padronizado — a
 * `EditContext API` ainda não é universal. Continua sendo o caminho com suporte
 * em todos os navegadores alvo, mas **não existe no jsdom**, então a ausência
 * não pode derrubar a renderização.
 */
function executar(comando: string, valor?: string): void {
  if (typeof document.execCommand !== 'function') return;
  document.execCommand(comando, false, valor);
}

const BOTOES = [
  { comando: 'bold', rotulo: 'Negrito', icone: Bold },
  { comando: 'italic', rotulo: 'Itálico', icone: Italic },
  { comando: 'underline', rotulo: 'Sublinhado', icone: Underline },
  { comando: 'strikeThrough', rotulo: 'Tachado', icone: Strikethrough },
  { comando: 'insertUnorderedList', rotulo: 'Lista com marcadores', icone: List },
  { comando: 'insertOrderedList', rotulo: 'Lista numerada', icone: ListOrdered },
] as const;

/** Os atalhos que qualquer editor tem. Sem eles, digitar a ficha fica mais lento. */
const ATALHOS: Record<string, string> = {
  b: 'bold',
  i: 'italic',
  u: 'underline',
};
